// src/components/Sidebar.jsx
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  List,
  ListItemIcon,
  ListItemText,
  Divider,
  ListItemButton,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Collapse,
  alpha,
  Stack,
  useTheme,
  useMediaQuery,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Code as CodeIcon,
  MenuBook as MenuBookIcon,
  People as PeopleIcon,
  Home as HomeIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Api as ApiIcon,
  CardTravel as CardTravelIcon,
  Business as BusinessIcon,
  WorkspacePremium as WorkspacePremiumIcon,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import apiClient from '../../apiClient';
import { PropertyContext } from '../../contexts/PropertyContext';
import { useTranslation } from 'react-i18next';
import { handleLogout } from '../../services/authService';
import { useUserRoles, useIsAuthenticated } from '../../hooks/useUserRoles';
import { FaSignOutAlt } from 'react-icons/fa';
import { styled } from '@mui/system';
import { LogoButton } from '../main/LogoButton';

const SidebarContainer = styled(Box)(({ theme }) => ({
  width: 280,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  overflowY: 'auto',
  overflowX: 'hidden',
  transition: 'width 0.3s ease',
  position: 'relative',
  zIndex: 1,
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.1),
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: alpha(theme.palette.primary.main, 0.2),
  },
  [theme.breakpoints.down('md')]: {
    height: '100%',
  },
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`,
  height: theme.components.MuiLayout.defaultProps.headerHeight.md,
  [theme.breakpoints.down('md')]: {
    height: theme.components.MuiLayout.defaultProps.headerHeight.xs,
  },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  margin: '2px 8px',
  borderRadius: theme.shape.borderRadius,
  padding: '8px 12px',
  transition: 'all 0.2s ease-in-out',
  fontSize: '0.875rem',
  '& .MuiListItemIcon-root': {
    minWidth: '40px',
    color: theme.palette.text.secondary,
    '& svg': {
      fontSize: '1.25rem',
    },
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
  },
  '&.active': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.16),
    },
  },
}));

const SidebarSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(0.5),
  padding: `0 ${theme.spacing(2)}`,
}));

const SidebarSectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: theme.spacing(1),
  paddingLeft: theme.spacing(1),
}));

const PropertyHeaderBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

const EnhancedPropertyManagementSection = ({ 
  properties, 
  selectedProperty, 
  handlePropertyChange, 
  handleItemClick, 
  isActiveRoute, 
  t, 
  theme 
}) => {
  const currentProperty = properties.find(p => p.id.toString() === selectedProperty);
  
  return (
    <>
      <SidebarSection>
        <SidebarSectionTitle>
          {t('sidebar.propertyManagement')}
        </SidebarSectionTitle>
      </SidebarSection>
      
      {/* Enhanced Property Selector */}
      <PropertyHeaderBox>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
          <Avatar 
            sx={{ 
              width: 32, 
              height: 32, 
              bgcolor: 'primary.main',
              fontSize: '0.875rem'
            }}
          >
            <BusinessIcon fontSize="small" />
          </Avatar>
          <Box flex={1}>
            <FormControl size="small" sx={{ width: '100%', minWidth: 120 }}>
              <Select
                value={selectedProperty || ''}
                onChange={handlePropertyChange}
                displayEmpty
                variant="standard"
                sx={{
                  '& .MuiSelect-select': {
                    padding: 0,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'text.primary',
                  },
                  '& .MuiInput-underline:before': {
                    display: 'none',
                  },
                  '& .MuiInput-underline:after': {
                    display: 'none',
                  },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                    display: 'none',
                  },
                }}
              >
                {properties.map((property) => (
                  <MenuItem key={property.id} value={property.id.toString()}>
                    {property.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
        
        {currentProperty && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip 
              label={`${currentProperty.activeCodeCount || 0} codes`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem', height: 20 }}
            />
            <Chip 
              label={`${currentProperty.users?.$values?.length || 0} users`}
              size="small" 
              variant="outlined"
              sx={{ fontSize: '0.75rem', height: 20 }}
            />
          </Stack>
        )}
      </PropertyHeaderBox>
      
      {/* Property Navigation Menu */}
      {selectedProperty && (
        <List sx={{ p: 1 }}>
          <StyledListItemButton
            component={Link}
            to={`/property/${selectedProperty}`}
            onClick={handleItemClick}
            className={isActiveRoute(`/property/${selectedProperty}`) ? 'active' : ''}
          >
            <ListItemIcon>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText primary={t('sidebar.propertyOverview')} primaryTypographyProps={{ fontWeight: 500 }} />
          </StyledListItemButton>
          
          <StyledListItemButton
            component={Link}
            to={`/property/${selectedProperty}/guest-trips`}
            onClick={handleItemClick}
            className={isActiveRoute(`/property/${selectedProperty}/guest-trips`) ? 'active' : ''}
          >
            <ListItemIcon>
              <CardTravelIcon />
            </ListItemIcon>
            <ListItemText primary={t('sidebar.guestTrips')} primaryTypographyProps={{ fontWeight: 500 }} />
          </StyledListItemButton>
          
          <StyledListItemButton
            component={Link}
            to={`/property/${selectedProperty}/codes`}
            onClick={handleItemClick}
            className={isActiveRoute(`/property/${selectedProperty}/codes`) ? 'active' : ''}
          >
            <ListItemIcon>
              <CodeIcon />
            </ListItemIcon>
            <ListItemText primary={t('sidebar.propertyCodes')} primaryTypographyProps={{ fontWeight: 500 }} />
          </StyledListItemButton>
          
          <StyledListItemButton
            component={Link}
            to={`/property/${selectedProperty}/users`}
            onClick={handleItemClick}
            className={isActiveRoute(`/property/${selectedProperty}/users`) ? 'active' : ''}
          >
            <ListItemIcon>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary={t('sidebar.propertyUsers')} primaryTypographyProps={{ fontWeight: 500 }} />
          </StyledListItemButton>
          
          <StyledListItemButton
            component={Link}
            to={`/property/${selectedProperty}/settings`}
            onClick={handleItemClick}
            className={isActiveRoute(`/property/${selectedProperty}/settings`) ? 'active' : ''}
          >
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary={t('sidebar.propertySettings')} primaryTypographyProps={{ fontWeight: 500 }} />
          </StyledListItemButton>
          
          <StyledListItemButton
            component={Link}
            to={`/property/${selectedProperty}/api-management`}
            onClick={handleItemClick}
            className={isActiveRoute(`/property/${selectedProperty}/api-management`) ? 'active' : ''}
          >
            <ListItemIcon>
              <ApiIcon />
            </ListItemIcon>
            <ListItemText primary={t('sidebar.apiManagement')} primaryTypographyProps={{ fontWeight: 500 }} />
          </StyledListItemButton>
          
          <StyledListItemButton
            component={Link}
            to="/partner/subscription"
            onClick={handleItemClick}
            className={isActiveRoute('/partner/subscription') ? 'active' : ''}
          >
            <ListItemIcon>
              <WorkspacePremiumIcon />
            </ListItemIcon>
            <ListItemText primary={t('sidebar.stripeSubscription')} primaryTypographyProps={{ fontWeight: 500 }} />
          </StyledListItemButton>
        </List>
      )}
    </>
  );
};

const AdminSection = ({ adminOpen, setAdminOpen, handleItemClick, isActiveRoute, isActiveRouteStartsWith, t }) => (
  <>
    <Divider sx={{ my: 1 }} />
    <SidebarSection>
      <SidebarSectionTitle>{t('sidebar.adminSection')}</SidebarSectionTitle>
    </SidebarSection>
    <List sx={{ p: 1 }}>
      <StyledListItemButton
        onClick={() => setAdminOpen(!adminOpen)}
        className={isActiveRouteStartsWith('/admin') ? 'active' : ''}
        aria-expanded={adminOpen ? 'true' : 'false'}
      >
        <ListItemIcon>
          <DashboardIcon />
        </ListItemIcon>
        <ListItemText primary={t('sidebar.adminDashboard')} primaryTypographyProps={{ fontWeight: 500 }} />
        {adminOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </StyledListItemButton>
      <Collapse in={adminOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <StyledListItemButton
            component={Link}
            to="/adminDashboard"
            onClick={handleItemClick}
            className={isActiveRoute('/adminDashboard') ? 'active' : ''}
            sx={{ pl: 4 }}
          >
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary={t('sidebar.overview')} primaryTypographyProps={{ fontWeight: 500 }} />
          </StyledListItemButton>
          <StyledListItemButton
            component={Link}
            to="/userManagement"
            onClick={handleItemClick}
            className={isActiveRoute('/userManagement') ? 'active' : ''}
            sx={{ pl: 4 }}
          >
            <ListItemIcon>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary={t('sidebar.userManagement')} primaryTypographyProps={{ fontWeight: 500 }} />
          </StyledListItemButton>
          <StyledListItemButton
            component={Link}
            to="/codeManagement"
            onClick={handleItemClick}
            className={isActiveRoute('/codeManagement') ? 'active' : ''}
            sx={{ pl: 4 }}
          >
            <ListItemIcon>
              <CodeIcon />
            </ListItemIcon>
            <ListItemText primary={t('sidebar.codeManagement')} primaryTypographyProps={{ fontWeight: 500 }} />
          </StyledListItemButton>
          <StyledListItemButton
            component={Link}
            to="/partnerManagement"
            onClick={handleItemClick}
            className={isActiveRoute('/partnerManagement') ? 'active' : ''}
            sx={{ pl: 4 }}
          >
            <ListItemIcon>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary={t('sidebar.partnerManagement')} primaryTypographyProps={{ fontWeight: 500 }} />
          </StyledListItemButton>
          <StyledListItemButton
            component={Link}
            to="/subscription-management"
            onClick={handleItemClick}
            className={isActiveRoute('/subscription-management') ? 'active' : ''}
            sx={{ pl: 4 }}
          >
            <ListItemIcon>
              <WorkspacePremiumIcon />
            </ListItemIcon>
            <ListItemText primary={t('sidebar.subscriptionManagement')} primaryTypographyProps={{ fontWeight: 500 }} />
          </StyledListItemButton>
          <StyledListItemButton
            component={Link}
            to="/feature-management"
            onClick={handleItemClick}
            className={isActiveRoute('/feature-management') ? 'active' : ''}
            sx={{ pl: 4 }}
          >
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Feature Management" primaryTypographyProps={{ fontWeight: 500 }} />
          </StyledListItemButton>
        </List>
      </Collapse>
    </List>
  </>
);

const Sidebar = ({ onLinkClick, showMobileLogo = false }) => {
  const { t } = useTranslation('general');
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const storedRoles = useUserRoles(); // Use custom hook for centralized role access
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedProperty, setSelectedProperty } = useContext(PropertyContext);
  const [properties, setProperties] = useState([]);
  const [adminOpen, setAdminOpen] = useState(true);

  const handleItemClick = () => {
    if (onLinkClick) onLinkClick();
  };

  useEffect(() => {
    if (storedRoles.includes('PartnerAdmin')) {
      const fetchProperties = async () => {
        try {
          const client = await apiClient(instance);
          const response = await client.get('/api/Partner/userPartners');
          
          // Extract the properties array from the nested $values structure
          const propertyArray = response.data.$values || [];
                    
          setProperties(propertyArray);
          
          if (propertyArray.length > 0) {
            const storedProperty = localStorage.getItem('selectedProperty');
            const isStoredPropertyValid = propertyArray.some(
              prop => prop.id.toString() === storedProperty
            );
            
            // Always ensure we're working with strings for the selectedProperty
            const newSelectedProperty = isStoredPropertyValid 
              ? storedProperty 
              : propertyArray[0].id.toString();

            setSelectedProperty(newSelectedProperty);
            localStorage.setItem('selectedProperty', newSelectedProperty);
          }
        } catch (error) {
          console.error('Error fetching properties:', error);
        }
      };
      fetchProperties();
    }
  }, [storedRoles, instance, setSelectedProperty]);

  const handlePropertyChange = (event) => {
    const newPropertyId = event.target.value;
    setSelectedProperty(newPropertyId);
    localStorage.setItem('selectedProperty', newPropertyId);
    navigate(`/property/${newPropertyId}`);
    if (onLinkClick) onLinkClick();
  };

  const handleLogoutClick = () => {
    handleLogout(instance);
  };

  const isActiveRoute = (route) => location.pathname === route;
  const isActiveRouteStartsWith = (path) => location.pathname.startsWith(path);

  return (
    <SidebarContainer className="sidebar-container">
      {/* Logo header */}
      {(!isMobileOrTablet || showMobileLogo) && (
        <SidebarHeader>
          <LogoButton />
        </SidebarHeader>
      )}
      
      {/* Main scrollable content */}
      <Box 
        className="sidebar-content"
        sx={{ 
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          position: 'relative',
          zIndex: 'auto',
        }}
      >
        {/* Navigation items */}
        <List component="nav" sx={{ p: 1 }}>
          <StyledListItemButton
            component={Link}
            to="/guides"
            onClick={handleItemClick}
            className={isActiveRoute('/guides') ? 'active' : ''}
          >
            <ListItemIcon>
              <MenuBookIcon />
            </ListItemIcon>
            <ListItemText primary={t('sidebar.guides')} />
          </StyledListItemButton>
          
          {/* My Trips for authenticated users */}
          {isAuthenticated && (
            <StyledListItemButton
              component={Link}
              to="/my-trips"
              onClick={handleItemClick}
              className={isActiveRoute('/my-trips') ? 'active' : ''}
            >
              <ListItemIcon>
                <CardTravelIcon />
              </ListItemIcon>
              <ListItemText primary="My Trips" />
            </StyledListItemButton>
          )}
        </List>

        <Divider sx={{ my: 1 }} />

        {/* Property Management Section */}
        {storedRoles.includes('PartnerAdmin') && properties.length > 0 && (
          <EnhancedPropertyManagementSection
            properties={properties}
            selectedProperty={selectedProperty}
            handlePropertyChange={handlePropertyChange}
            handleItemClick={handleItemClick}
            isActiveRoute={isActiveRoute}
            t={t}
            theme={theme}
          />
        )}

        {/* Admin Section */}
        {storedRoles.includes('Admin') && (
          <AdminSection
            adminOpen={adminOpen}
            setAdminOpen={setAdminOpen}
            handleItemClick={handleItemClick}
            isActiveRoute={isActiveRoute}
            isActiveRouteStartsWith={isActiveRouteStartsWith}
            t={t}
          />
        )}
      </Box>

      {/* Footer section (logout and potentially other buttons) */}
      <Box sx={{ mt: 'auto', p: 1 }}>
        <Divider />
        <List component="nav" disablePadding>
          {isAuthenticated && (
            <StyledListItemButton
              onClick={handleLogoutClick}
              sx={{
                color: theme.palette.error.main,
                mt: 1,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>
                <FaSignOutAlt />
              </ListItemIcon>
              <ListItemText primary={t('sidebar.logout')} />
            </StyledListItemButton>
          )}
          {/* Future buttons would go here */}
        </List>
      </Box>
    </SidebarContainer>
  );
};

export default Sidebar;